/**
 * `resolveFieldPath` — compute the full rhf register path for a field.
 *
 * Top-level fields register against their own `name` (e.g. `"pib"`).
 * Repeater rows scope their nested fields under a dotted path that
 * carries the parent name and the row index, e.g. `"partners.0.pib"`.
 *
 * Field components call this helper instead of using `field.name`
 * directly so that the same component code works inside or outside a
 * Repeater. The path is also reused as the input `id`, the `<Label>`'s
 * `htmlFor`, the lookup key into `formState.errors`, and (where present)
 * the value of constructed sub-ids like `${path}-caption`.
 *
 * Why a tiny helper rather than inlining the ternary at every site:
 * twelve field components compute this same expression. Centralising
 * keeps the format ("dot-joined") consistent with rhf's nested-path
 * convention and gives us a single place to fix if the format ever
 * changes (it shouldn't — rhf's API is stable here).
 *
 * Note: rhf accepts either dotted (`a.b.c`) or bracketed (`a[0].c`) paths
 * for nested fields. Dotted is the canonical representation used in the
 * library's own docs and examples, and reads better in our error map
 * where keys are flat strings.
 */
export function resolveFieldPath(name: string, pathPrefix?: string): string {
  return pathPrefix !== undefined && pathPrefix.length > 0
    ? `${pathPrefix}.${name}`
    : name;
}
